import { membershipNameType } from './constant'

/**
 * Win-back promotion — Standard and Pro, September 2026.
 *
 * Built for the Meta retargeting ad set, which is aimed at people who signed up, read a
 * few stories and stopped.
 *
 * Offer: 50% off the first 3 months on **Standard or Pro**, monthly billing.
 *
 * Deliberately both plans, unlike DRAGON_BOAT_PROMO which was Pro only. 82% of paying
 * subscribers are on Standard, so a Pro-only discount would show most arrivals an offer
 * they cannot take, which is a worse failure than the mismatch it avoids. The ad sells
 * the teacher and the teacher is on Pro; the plan page can make that case on its own
 * without the discount being withheld from everyone who came for the reading.
 *
 * Monthly only, on both. The Stripe coupon carries the "3 months" via duration=repeating
 * and duration_in_months=3, which has no sensible meaning against a yearly charge. The
 * frontend only communicates it.
 *
 * Same shape as DRAGON_BOAT_PROMO, deliberately: date-gated, no feature flag, no
 * commented-out code. When the window closes every surface reverts on its own.
 *
 * Audience (who is offered the upgrade):
 *   - Free learners (this includes the weekly_test_cohort free learners)
 *   - Weekly subscribers
 * Standard, Pro and Teacher learners are intentionally excluded. This is a win-back for
 * people who are not paying, not an upsell for people who already are.
 *
 * Prices are NEVER hardcoded here. Each plan's regular monthly price comes live from
 * Stripe and the promo price is derived from it (see `getWinBackPromoPricing`). Only the
 * campaign framing lives in this module.
 *
 * ------------------------------------------------------------------------------------
 * TWO THINGS MUST BE TRUE BEFORE THIS SHIPS
 *
 *   1. DONE, 10 Sep 2026. Coupon `meta-sep26-50-3m` exists in the live account: 50% off,
 *      repeating, 3 months, scoped to Standard and Pro, interval_scope month, redeem_by
 *      15 Sep 23:59 HKT.
 *
 *   2. The Meta offer ad must be scheduled to END with `endDate` below. If the creative
 *      outlives the coupon, people land on full price with no explanation.
 *
 * Do NOT reuse `churn-save-50-3m`. That is the retention coupon handed out in the cancel
 * flow, it is claimable once ever per customer, and sharing it would either let people
 * double dip or burn a churning subscriber's save offer.
 * ------------------------------------------------------------------------------------
 */
export const WIN_BACK_PROMO = {
  // Must match the Stripe coupon id exactly. Created 10 Sep 2026 as `meta-sep26-50-3m`,
  // 50% off, repeating 3 months, scoped to Standard and Pro, interval_scope month.
  couponCode: 'meta-sep26-50-3m',
  billing: 'monthly' as const,
  // Anchored to Hong Kong time (UTC+8), matching the rest of the app's story cadence.
  //
  // The end date is tied to the coupon's redeem_by, which is 15 Sep 23:59 HKT. We close
  // the window an hour early on purpose: a learner who opens checkout at 23:58 and pays
  // at 00:01 would be shown half price and charged full, because Stripe refuses an
  // expired coupon at the moment of payment, not at the moment the page rendered.
  startDate: new Date('2026-09-10T00:00:00+08:00'), // Sep 10, 12:00am HKT
  endDate: new Date('2026-09-15T23:00:00+08:00'), // Sep 15, 11:00pm HKT, coupon dies at 23:59
  discountPercent: 50,
  durationMonths: 3,
  // Shown on the plan cards while the promo is live. No plan name and no em dash: this
  // string ships in front of learners and obeys the ad copy rules.
  badgeLabel: 'Half price for three months',
} as const

export type PromoTimeLeft = {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const emptyTimeLeft = (): PromoTimeLeft => ({ days: 0, hours: 0, minutes: 0, seconds: 0 })

export function isWinBackPromoActive(now: Date = new Date()): boolean {
  return now >= WIN_BACK_PROMO.startDate && now <= WIN_BACK_PROMO.endDate
}

export function getWinBackPromoEndDate(): Date {
  return WIN_BACK_PROMO.endDate
}

export function getWinBackPromoTimeLeft(now: Date = new Date()): PromoTimeLeft {
  const diff = WIN_BACK_PROMO.endDate.getTime() - now.getTime()
  if (diff <= 0) return emptyTimeLeft()
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  }
}

/**
 * Derive the displayed pricing from a plan's live Stripe monthly price. Returns null when
 * the price is unavailable so callers can render a loading state instead of a made-up
 * number.
 */
export function getWinBackPromoPricing(
  regularMonthlyPrice?: number | null,
): { regularMonthly: number; promoMonthly: number } | null {
  if (!regularMonthlyPrice || isNaN(regularMonthlyPrice) || regularMonthlyPrice <= 0) return null
  const promoMonthly = (regularMonthlyPrice * (100 - WIN_BACK_PROMO.discountPercent)) / 100
  return { regularMonthly: regularMonthlyPrice, promoMonthly }
}

export type WinBackAudienceContext = {
  membershipName?: string
  now?: Date
}

/**
 * Pure audience test (ignores the date window): is this learner someone the win-back
 * upgrade is aimed at? Free (incl. weekly_test_cohort) or Weekly.
 */
export function isWinBackAudience({ membershipName }: WinBackAudienceContext): boolean {
  if (!membershipName) return false
  if (membershipName === membershipNameType.Free) return true
  if (membershipName === membershipNameType.Weekly) return true
  // Standard, Pro, Teacher — not the audience.
  return false
}

/** Active window AND in-audience: the gate for showing the offer on any surface. */
export function isUserEligibleForWinBackPromo(ctx: WinBackAudienceContext): boolean {
  const now = ctx.now ?? new Date()
  return isWinBackPromoActive(now) && isWinBackAudience({ ...ctx, now })
}

/** The plans this campaign discounts. Monthly billing only, see the note above. */
const DISCOUNTED_PLANS: readonly string[] = [membershipNameType.Standard, membershipNameType.Pro]

/**
 * Whether a /setting#plan card should render the promo treatment. Standard or Pro, on
 * monthly billing, for an eligible learner, when no manual coupon is applied and it is
 * not already their current plan.
 */
export function shouldApplyWinBackPromoToPlan({
  planValue,
  billing,
  isCurrentPlan,
  hasManualCoupon,
  membershipName,
  now = new Date(),
}: {
  planValue?: string
  billing: 'monthly' | 'yearly'
  isCurrentPlan: boolean
  hasManualCoupon: boolean
  membershipName?: string
  now?: Date
}): boolean {
  if (!isWinBackPromoActive(now)) return false
  if (hasManualCoupon) return false
  if (isCurrentPlan) return false
  if (!planValue || !DISCOUNTED_PLANS.includes(planValue)) return false
  if (billing !== WIN_BACK_PROMO.billing) return false
  return isWinBackAudience({ membershipName })
}
